import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from 'src/app/core/reducers';
import { CategoryService, selectTemplatesActionLoading } from 'src/app/core/lists';
import { take } from 'rxjs/operators';
import { Template } from '../../../../../../core/lists/_models/template.model';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { clone } from 'lodash-es';
import { UserService } from '../../../../../../core/auth/_services';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { ProgramService, StandardService } from '../../../../compliance/services';
import { Program, Standard } from '../../../../compliance/models';
import { TemplateType } from '../../../../../../core/enums';
import { TemplateService } from '../../../../../../core/lists/_services/template.service';
import { LayoutUtilsService, MessageType } from '../../../../../../core/_base/crud';
import { Form } from '../../../../forms-templates/models';
import { FormService } from '../../../../forms-templates/services';
import {
  CreateFormComponent,
  CreateFormSynonymLinkComponent,
} from '../../../../forms-templates/components';
import { TaskActionsService } from '../../../../../../core/services/task-actions.service';
import { currentUser } from '../../../../../../core/auth';

@Component({
  selector: 'kt-template-edit',
  templateUrl: './template-edit.component.html',
  styleUrls: ['./template-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TemplateEditComponent implements AfterViewInit, OnInit, OnDestroy {
  template: Template;
  templateForm: FormGroup;
  hasFormErrors = false;
  viewLoading = false;
  subscriptions: Subscription[] = [];
  isReadOnly = false;
  categoryAutocompleteCall;
  @ViewChild('categoryAutocomplete') categoryAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('programAutocomplete') programAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('followerAutocomplete') followerAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('assigneeAutocomplete') assigneeAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('wizard', { static: true }) el: ElementRef;
  assigneeAutocompleteCall = this.userService.searchUsers.bind(this.userService);
  priorityOptions = ['HIGH', 'MEDIUM', 'LOW'];
  getProgramAutocomplete = this.programService.searchProgram.bind(this.programService);
  getStandardAutocomplete = this.standardService.searchStandard.bind(this.standardService);
  editUrlShow = true;
  disableEditing = false;
  TemplateType = TemplateType;
  isSubmit = false;
  getFormAutocomplete = this.formService.findForms.bind(this.formService, {});
  @ViewChild('formAutocomplete') formAutocomplete: GeneralAutocompleteComponent;
  defaultViewValue;
  reloadActions = true;
  firstStep = true;
  emittedAdditionalSteps;
  emittedCanAddAdditionalSteps;
  mode = '';
  userInfo: any;
  userType = '';
  lockedByAdmin = false;
  private viewInitialized = false;
  private formDataReady = false;

  ngAfterViewInit(): void {
    this.viewInitialized = true;



    // Initialize form wizard
    const wizard = new KTWizard(this.el.nativeElement, {
      startStep: 1,
    });

    wizard.on('beforePrev', (wizardObj) => {
      if (wizardObj.currentStep === 2) {
        this.firstStep = true;
        this.cdr.detectChanges();
      }
    });
    wizard.on('beforeNext', (wizardObj) => {
      // this.validation(wizardObj);
      if (wizardObj.currentStep === 1) {
        this.onSubmit(wizardObj);
      }
      if (this.defaultViewValue === 'CHECK_LIST') {
        wizardObj.totalSteps = 9;
        this.cdr.detectChanges();
        return;
      }
      wizardObj.totalSteps = 8;
      this.cdr.detectChanges();
    });

    // Change event
    wizard.on('change', () => {
      setTimeout(() => {
        KTUtil.scrollTop();
      }, 500);
    });
    if (this.formDataReady) {
      this.patchAutocompletes();
    }
  }
  private patchAutocompletes(): void {
    if (!this.template.id) return;

    setTimeout(() => {
      this.categoryAutocomplete?.setValue(this.data.prevCategory);
      this.categoryAutocomplete?.updateValueAndValidity();

      const selectedProgram = this.getProgramFromStandards();

      this.templateForm.get('program')?.setValue(selectedProgram);
      this.templateForm.get('program')?.updateValueAndValidity();

      this.programAutocomplete?.setValue(selectedProgram);
      this.programAutocomplete?.updateValueAndValidity();
      if (selectedProgram?.id) {
        this.getStandardAutocomplete = this.standardService.searchStandard.bind(
          this.standardService,
          selectedProgram.id,
          false,
        );
      }
      this.formAutocomplete?.setValue(this.template?.form);
      this.formAutocomplete?.updateValueAndValidity();
      this.assigneeAutocomplete?.setValue(this.template?.task_responsible_users);
      this.assigneeAutocomplete?.updateValueAndValidity();
      this.followerAutocomplete?.setValue(this.template?.task_followers);
      this.followerAutocomplete?.updateValueAndValidity();

      this.cdr.detectChanges();
    }, 0);
  }
  getProgramFromStandards(): Program {
    const standards = this.data?.prevStandards || [];

    if (standards.length && standards[0]?.program) {
      return standards[0].program;
    }

    return null;
  }

  filterCategoryFn(event: any): string {
    return event ? event.name : '';
  }

  onCategoryChange(val) {
    this.templateForm.get('category').setValue(val);

    this.formAutocomplete?.setValue(null);
    this.formAutocomplete?.updateValueAndValidity();
    this.getFormAutocomplete = this.formService.findForms.bind(this.formService, {
      teamId: val?.id,
    });
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private translate: TranslateService,
    private categoryService: CategoryService,
    public datepipe: DatePipe,
    private cdr: ChangeDetectorRef,
    private userService: UserService,
    public stringUtils: StringUtilsService,
    private programService: ProgramService,
    private standardService: StandardService,
    private formService: FormService,
    private taskActionsService: TaskActionsService,
    private layoutUtils: LayoutUtilsService,
    private templateService: TemplateService,
    public dialog: MatDialog,
  ) {
    this.store.select(currentUser).subscribe((res) => (this.userInfo = res));
    this.userType = '';
    if (this.userInfo?.is_manager) this.userType = 'manager';
    if (this.userInfo?.userType === 'Admin') this.userType = 'admin';
    if (this.userInfo?.userType === 'User' && !this.userInfo?.is_manager) this.userType = 'user';
    this.categoryAutocompleteCall = this.categoryService.getCategoriesInTemplateByUserType.bind(
      this.categoryService,
      this.userType,
    );
  }

  ngOnInit() {
    this.mode = this.data.mode;
    this.template = this.data.template ? this.data.template : new Template();

    if (this.userType === 'user') {
      this.mode = 'view';
    }
    this.isReadOnly = this.mode === 'view';

    if (this.userType === 'manager' && this.template.is_locked) {
      this.lockedByAdmin = true;
      this.isReadOnly = true;
    }

    const loadingSub = this.store
      .pipe(select(selectTemplatesActionLoading))
      .subscribe((res) => (this.viewLoading = res));
    this.subscriptions.push(loadingSub);

    this.createForm();
    this.formDataReady = true;

    if (this.template.form?.id) {
      this.formService
        .getFormById(this.template.form.id)
        .pipe(take(1))
        .subscribe(({ data }) => {
          this.template = { ...this.template, form: data };
          this.createForm();
          this.formDataReady = true;
          if (this.viewInitialized) {
            this.patchAutocompletes();
          }
          this.cdr.detectChanges();
        });
    }
  }
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  createForm() {
    this.defaultViewValue = this.template.default_view;
    this.templateForm = this.fb.group({
      name: [this.template.name, Validators.required],
      category: [null, Validators.required],
      default_view: [this.template.default_view],
      title: [this.template.title, Validators.required],
      description: [this.template.description],
      all_roles: [this.template.all_roles || false],
      allow_payment: [this.template.allow_payment || false],
      is_backstage: [this.template.is_backstage || false],
      is_service: [this.template.is_service || false],
      is_private_service_allowed: [this.template.is_private_service_allowed || false],
      is_locked: [this.template.is_locked || false],
      is_active: [this.template.is_active !== false],
      priority: [this.template.priority],
      task_responsible_users: [],
      task_followers: [],
      isExclude: [this.template.exclude_from_filtered_view || false],
      program: [this.getProgramFromStandards()],
      url_resource: [
        this.template.url_resource,
        Validators.pattern(
          'https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_+.~#?&\\/=]*)',
        ),
      ],
      standards: [this.template.id && [...this.data.prevStandards]],
      objective: [this.template.objective || ''],
      critical_data: [this.template.critical_data || ''],
      data_sources: [this.template.data_sources || ''],
      authorized_communication_methods: [
        this.communicationMethodsFrom(this.template.authorized_communication_methods),
      ],
    });
    if (this.mode === 'view' || this.lockedByAdmin) {
      this.templateForm.disable();
      return;
    }
    this.bindServiceFlagRules();
  }

  private bindServiceFlagRules(): void {
    this.applyServiceFlagRules();
    this.subscriptions.push(
      this.templateForm
        .get('default_view')
        .valueChanges.subscribe(() => this.applyServiceFlagRules()),
      this.templateForm
        .get('is_service')
        .valueChanges.subscribe(() => this.applyServiceFlagRules()),
    );
  }

  private applyServiceFlagRules(): void {
    const view = this.templateForm.get('default_view').value;
    const serviceAllowed = view === 'FORM' || view === 'CHECK_LIST';
    const serviceCtrl = this.templateForm.get('is_service');
    const privateCtrl = this.templateForm.get('is_private_service_allowed');

    if (!serviceAllowed) {
      if (serviceCtrl.value) serviceCtrl.setValue(false, { emitEvent: false });
      if (privateCtrl.value) privateCtrl.setValue(false, { emitEvent: false });
      serviceCtrl.disable({ emitEvent: false });
      privateCtrl.disable({ emitEvent: false });
      return;
    }

    serviceCtrl.enable({ emitEvent: false });
    if (!serviceCtrl.value) {
      if (privateCtrl.value) privateCtrl.setValue(false, { emitEvent: false });
      privateCtrl.disable({ emitEvent: false });
      return;
    }
    privateCtrl.enable({ emitEvent: false });
  }

  prepareTemplate(): Template {
    const model = clone(this.templateForm.value);
    const _template = new Template();
    const prevStandardIds =
      this.data.prevStandards && this.data.prevStandards.length
        ? this.data.prevStandards.map((standard) => standard.id)
        : [];
    const curStandardIds =
      model.standards && model.standards.length
        ? model.standards.map((standard) => standard.id)
        : [];
    const isStandardEdited =
      this.template.id &&
      JSON.stringify(prevStandardIds.sort()) !== JSON.stringify(curStandardIds.sort());
    _template.id = this.template.id;
    _template.behavior = this.template.behavior;
    _template.category_id = model.category ? model.category.id : null;
    _template.name = model.name?.trim();
    _template.title = model.title?.trim();
    _template.description = model.description;
    _template.url_resource = model.url_resource;
    _template.priority = model.priority;
    _template.task_followers = model.task_followers;
    _template.task_responsible_users = model.task_responsible_users;
    _template.all_roles = model.all_roles;
    _template.allow_payment = model.allow_payment;
    _template.is_backstage = model.is_backstage;
    const rawFlags = this.templateForm.getRawValue();
    _template.is_service = rawFlags.is_service;
    _template.is_private_service_allowed = rawFlags.is_private_service_allowed;
    _template.exclude_from_filtered_view = model.isExclude;
    _template.compliance = { ids: curStandardIds, is_edited: isStandardEdited };
    _template.form = this.formAutocomplete?.value;
    _template.default_view = model.default_view;
    _template.is_locked = model.is_locked;
    _template.is_active = model.is_active;
    _template['program_id'] = model.program ? model.program.id : null;
    _template.objective = model.objective || '';
    _template.critical_data = model.critical_data || '';
    _template.data_sources = model.data_sources || '';
    _template.authorized_communication_methods = model.authorized_communication_methods || [];

    return _template;
  }

  private communicationMethodsFrom(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }

  onSubmit(wizardObj = null) {
    if (this.mode === 'view' || this.lockedByAdmin) return;
    this.hasFormErrors = false;
    const controls = this.templateForm.controls;
    this.categoryAutocomplete.updateValueAndValidity();
    this.assigneeAutocomplete.updateValueAndValidity();
    this.followerAutocomplete.updateValueAndValidity();
    this.formAutocomplete?.updateValueAndValidity();
    let invalidForm = false;
    if (this.defaultViewValue === 'FORM' && !this.formAutocomplete?.value) {
      invalidForm = true;
    }
    if (this.templateForm.invalid || invalidForm) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.hasFormErrors = true;
      if (wizardObj) {
        wizardObj.stop();
        this.firstStep = true;
        this.cdr.detectChanges();
      }
      return;
    }
    this.firstStep = false;
    this.isSubmit = true;
    setTimeout(() => {
      const editedTemplate = this.prepareTemplate();

      if (editedTemplate.id > 0) {
        this.updateTemplate(editedTemplate);
      } else {
        this.createTemplate(editedTemplate);
      }
    });
  }

  updateTemplate(_template: Template) {
    this.templateService.updateTemplate(_template).subscribe(() => {
      this.layoutUtils.showActionNotification(
        'Template updated successfully',
        MessageType.Update,
        3000,
        false,
        false,
      );
    });
  }
  close() {
    if (this.mode === 'view' || this.lockedByAdmin) {
      this.templateService.closeLatestDialog({ view: true });
    }
    if (this.data.mode === 'additional_step') {
      this.templateService.closeLatestDialog({ template: this.template, created: true });
      return;
    }
    this.templateService.closeLatestDialog({ updated: true });
  }

  createTemplate(_template: Template) {
    this.templateService.createTemplate(_template).subscribe((result) => {
      this.template = result.data;
      this.cdr.detectChanges();
      this.layoutUtils.showActionNotification(
        'Template added successfully',
        MessageType.Create,
        3000,
        false,
        false,
      );
    });
  }

  onAlertClose() {
    this.hasFormErrors = false;
  }
  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.templateForm.controls[controlName];
    if (!control) {
      return false;
    }

    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  getStatusString(val): string {
    switch (val) {
      case true:
        return 'Yes';
      case false:
        return 'No';
    }
    return '';
  }

  filterAssigneeFn(assignee: any): string {
    let res = '';
    if (assignee) {
      res += assignee.fullName;
    }
    if (assignee && assignee.name) {
      res += '  ' + `<small class="text-muted">` + assignee.name + `</small>`;
    }
    return res;
  }

  formatNewAssigneeFn(item: any) {
    return {
      responsible_id: item.id,
      fullName: item.fullName,
      name: item.name,
    };
  }

  formatNewFollowerFn(item: any) {
    return {
      follower_id: item.id,
      fullName: item.fullName,
      name: item.name,
    };
  }

  onAssigneeChange(val) {
    const users = val.map((item) => {
      if (item && !item.responsible_id) {
        return {
          responsible_id: item.id,
          fullName: item.fullName,
          name: item.name,
        };
      }
      return item;
    });
    this.templateForm.controls.task_responsible_users.setValue(users);
    this.templateForm.controls.task_responsible_users.updateValueAndValidity();
  }

  onFollowerChange(val) {
    const users = val.map((item) => {
      if (item && !item.follower_id) {
        return {
          follower_id: item.id,
          fullName: item.fullName,
          name: item.name,
        };
      }
      return item;
    });
    this.templateForm.controls.task_followers.setValue(users);
    this.templateForm.controls.task_followers.updateValueAndValidity();
  }

  getPriority(priority) {
    if (priority === 'HIGH') return 'svg-icon svg-icon-danger svg-icon-2x';
    if (priority === 'MEDIUM') return 'svg-icon svg-icon-warning svg-icon-2x';
    return 'svg-icon svg-icon-2x';
  }

  getPriorityText(priority) {
    switch (priority) {
      case 'HIGH':
        return this.translate.instant('TASK.HIGH');
      case 'MEDIUM':
        return this.translate.instant('TASK.MEDIUM');
      case 'LOW':
        return this.translate.instant('TASK.LOW');
      default:
        return '';
    }
  }

  formatProgramOption(program: Program): string {
    return program ? program.name : '';
  }

  onProgramChange(program: Program): void {
    this.templateForm.get('program').patchValue(program);
    this.getStandardAutocomplete = this.standardService.searchStandard.bind(
      this.standardService,
      program?.id,
      false,
    );
  }

  formatStandardOption(standard: Standard): string {
    return standard
      ? `${standard.standard_reference}<small class="text-muted ml-4">${
          standard.program ? standard.program.name : ''
        }</small>`
      : '';
  }

  formatNewStandard(standard: Standard): Standard {
    return standard;
  }

  onStandardChange(standards: Standard[]): void {
    this.templateForm.get('standards').patchValue(standards);

    if (standards?.length && standards[0]?.program) {
      const selectedProgram = standards[0].program;

      this.templateForm.get('program')?.setValue(selectedProgram);
      this.templateForm.get('program')?.updateValueAndValidity();

      this.programAutocomplete?.setValue(selectedProgram);
      this.programAutocomplete?.updateValueAndValidity();

      this.getStandardAutocomplete = this.standardService.searchStandard.bind(
        this.standardService,
        selectedProgram.id,
        false,
      );
    }
  }

  validateUrl() {
    if (this.showExternalLinkIcon()) {
      this.editUrlShow = true;
    }
  }

  showExternalLinkIcon() {
    const url = this.templateForm.get('url_resource').value;
    return url && url.length > 0 && !this.isControlHasError('url_resource', 'pattern');
  }

  openExternalLink() {
    window.open(this.templateForm.get('url_resource').value, '_blank');
  }

  formatFormOption(form: Form): string {
    return form?.name || '';
  }

  viewForm(event: Event): void {
    event.stopPropagation();

    const form = this.formAutocomplete?.value;
    if (!form) {
      this.formAutocomplete?.recorderInput?.nativeElement.focus();

      return;
    }

    if (form.teams?.length)
      this.categoryService
        .getTeamByIds(form.teams)
        .pipe(take(1))
        .subscribe((res) => {
          const dialogConfig = new MatDialogConfig();

          form.teams = res?.data;

          dialogConfig.disableClose = true;
          dialogConfig.closeOnNavigation = true;
          dialogConfig.width = '600px';
          dialogConfig.data = { form, viewMode: true };

          this.dialog.open(CreateFormComponent, dialogConfig);
        });
  }

  linkFormFieldsToSynonyms(event: Event): void {
    event.stopPropagation();

    const form = this.formAutocomplete?.value;

    if (!form) {
      this.formAutocomplete?.recorderInput?.nativeElement.focus();

      return;
    }

    const message = this.translate.instant('FORMS_TEMPLATES_MODULE.FORM.LINK_MESSAGE');
    const messageType = MessageType.Update;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '1000px';
    dialogConfig.data = form;

    const dialogRef = this.dialog.open(CreateFormSynonymLinkComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        this.layoutUtils.showActionNotification(message, messageType, 3000, false, false);
      });
  }
  additionalStepValue(val) {
    this.emittedAdditionalSteps = val;
    this.taskActionsService.updateList(val);
    this.cdr.detectChanges();
  }
  setAdditionalStepValue(val) {
    this.emittedCanAddAdditionalSteps = val;
    this.taskActionsService.updateList(val);
    this.cdr.detectChanges();
  }

  getButtonText() {
    if (this.firstStep) {
      if (this.mode === 'view' || this.lockedByAdmin) return 'Next Step';
      return 'Save Template';
    }
    return 'Next Step';
  }
}
